import React, { useEffect, useState, useCallback } from 'react';
import { countryToFlag } from '../../utils/countryFlags';

const API = process.env.REACT_APP_BACKEND_URL;

const Card = ({ title, children, testId }) => (
  <div
    data-testid={testId}
    className="bg-zinc-900/80 backdrop-blur-xl border border-red-900/30 rounded-lg p-6"
  >
    <h3 className="text-xl font-black text-white mb-4 tracking-wide uppercase">{title}</h3>
    {children}
  </div>
);

const StatPill = ({ label, value, testId }) => (
  <div
    data-testid={testId}
    className="bg-zinc-900/60 border border-red-900/40 rounded-lg px-5 py-4"
  >
    <p className="text-gray-400 text-xs uppercase tracking-widest mb-1">{label}</p>
    <p className="text-3xl font-black text-white">{Number(value).toLocaleString()}</p>
  </div>
);

const formatTime = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch {
    return iso;
  }
};

const CountryRow = ({ country, count, unique, percent, max }) => (
  <div data-testid={`geo-country-row-${country}`} className="py-3 border-b border-red-900/10 last:border-0">
    <div className="flex items-center justify-between mb-1.5 gap-3">
      <div className="flex items-center gap-3 min-w-0">
        <span className="text-2xl leading-none">{countryToFlag(country)}</span>
        <span className="text-white font-bold truncate">{country}</span>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <span className="text-gray-400 text-xs">{unique} unique</span>
        <span className="text-white font-black tabular-nums">{count.toLocaleString()}</span>
        <span className="text-red-400 font-bold text-xs w-12 text-right tabular-nums">{percent}%</span>
      </div>
    </div>
    <div className="h-2 w-full bg-zinc-800/80 rounded-full overflow-hidden">
      <div
        className="h-full bg-gradient-to-r from-red-700 to-red-500 rounded-full"
        style={{ width: `${max ? (count / max) * 100 : 0}%` }}
      />
    </div>
  </div>
);

// Simple equirectangular projection world map with dots for visitor clusters
const WorldMap = ({ points }) => {
  if (!points || points.length === 0) {
    return (
      <p className="text-gray-500 text-sm italic" data-testid="geo-map-empty">
        No geolocated visitors yet. Dots will appear here as traffic comes in.
      </p>
    );
  }
  const W = 1000;
  const H = 500;
  const project = (lat, lon) => {
    const x = ((lon + 180) / 360) * W;
    const y = ((90 - lat) / 180) * H;
    return { x, y };
  };
  const maxCount = Math.max(...points.map((p) => p.count));

  return (
    <div className="relative w-full overflow-hidden rounded-lg bg-zinc-950 border border-red-900/20" data-testid="geo-world-map">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto block">
        {/* Background gradient */}
        <defs>
          <radialGradient id="dotGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#ef4444" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#ef4444" stopOpacity="0" />
          </radialGradient>
          <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
            <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#1f1f1f" strokeWidth="0.5" />
          </pattern>
        </defs>
        <rect width={W} height={H} fill="#09090b" />
        <rect width={W} height={H} fill="url(#grid)" />
        {/* Equator and prime meridian */}
        <line x1="0" y1={H / 2} x2={W} y2={H / 2} stroke="#27272a" strokeWidth="0.5" strokeDasharray="2 4" />
        <line x1={W / 2} y1="0" x2={W / 2} y2={H} stroke="#27272a" strokeWidth="0.5" strokeDasharray="2 4" />

        {points.map((p, i) => {
          const { x, y } = project(p.lat, p.lon);
          const intensity = p.count / maxCount;
          const r = 4 + intensity * 16;
          return (
            <g key={`${p.lat}-${p.lon}-${i}`}>
              <circle cx={x} cy={y} r={r * 1.8} fill="url(#dotGlow)" opacity={0.5} />
              <circle cx={x} cy={y} r={Math.max(3, r * 0.45)} fill="#dc2626" stroke="#fff" strokeWidth="0.7">
                <title>{`${p.city || ''}${p.city ? ', ' : ''}${p.country || ''} - ${p.count} views`}</title>
              </circle>
            </g>
          );
        })}
      </svg>
    </div>
  );
};

const GeographyTab = ({ token }) => {
  const [geo, setGeo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchGeo = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/geo`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to load geo data');
      const data = await res.json();
      setGeo(data);
    } catch (e) {
      setError(e.message || 'Failed to load geo data');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchGeo();
  }, [fetchGeo]);

  if (loading) {
    return (
      <div className="text-gray-400" data-testid="geo-loading">
        Loading geographic data...
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="bg-red-900/20 border border-red-500/50 text-red-400 px-4 py-3 rounded"
        data-testid="geo-error"
      >
        {error}
      </div>
    );
  }

  if (!geo) return null;

  const topCountryCount = geo.countries[0]?.count || 1;

  return (
    <div className="space-y-8" data-testid="geography-tab">
      {/* Top stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatPill label="Geolocated Views" value={geo.totals.located_views} testId="geo-stat-views" />
        <StatPill label="Countries Reached" value={geo.totals.countries_count} testId="geo-stat-countries" />
        <StatPill label="Cities Reached" value={geo.totals.cities_count} testId="geo-stat-cities" />
      </div>

      {/* World map */}
      <Card title="Live Visitor Map" testId="geo-card-map">
        <WorldMap points={geo.map_points} />
        <p className="text-xs text-gray-500 mt-3">
          Dot size = visit volume. Hover a dot for details.
        </p>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Countries leaderboard */}
        <Card title="Visitors by Country" testId="geo-card-countries">
          {geo.countries.length === 0 ? (
            <p className="text-gray-500 italic text-sm">No location data yet.</p>
          ) : (
            <div className="max-h-[520px] overflow-y-auto pr-2 -mr-2">
              {geo.countries.map((c) => (
                <CountryRow
                  key={c.country}
                  country={c.country}
                  count={c.count}
                  unique={c.unique}
                  percent={c.percent}
                  max={topCountryCount}
                />
              ))}
            </div>
          )}
        </Card>

        {/* Cities */}
        <Card title="Top Cities" testId="geo-card-cities">
          {geo.cities.length === 0 ? (
            <p className="text-gray-500 italic text-sm">No city data yet.</p>
          ) : (
            <ol className="space-y-2" data-testid="geo-cities-list">
              {geo.cities.map((c, idx) => (
                <li
                  key={`${c.city}-${c.country}-${idx}`}
                  data-testid={`geo-city-${idx}`}
                  className="flex items-center justify-between bg-zinc-950/60 border border-red-900/10 rounded px-3 py-2"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="text-red-500 font-black tabular-nums w-6 text-right">
                      {idx + 1}
                    </span>
                    <span className="text-xl leading-none">{countryToFlag(c.country)}</span>
                    <div className="min-w-0">
                      <p className="text-white font-bold truncate">{c.city}</p>
                      <p className="text-gray-500 text-xs truncate">{c.country}</p>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-white font-black tabular-nums">{c.count.toLocaleString()}</p>
                    <p className="text-gray-500 text-xs">{c.unique} unique</p>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </Card>
      </div>

      {/* Recent visitors feed */}
      <Card title="Recent Visitors" testId="geo-card-recent">
        {geo.recent.length === 0 ? (
          <p className="text-gray-500 italic text-sm">No recent located visitors.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-400 uppercase text-xs tracking-wider border-b border-red-900/30">
                  <th className="pb-2 pr-4">When</th>
                  <th className="pb-2 pr-4">Location</th>
                  <th className="pb-2 pr-4">Device</th>
                  <th className="pb-2">Page</th>
                </tr>
              </thead>
              <tbody data-testid="geo-recent-list">
                {geo.recent.map((r, idx) => (
                  <tr
                    key={`${r.ip}-${r.timestamp}-${idx}`}
                    data-testid={`geo-recent-row-${idx}`}
                    className="border-b border-red-900/10 last:border-0"
                  >
                    <td className="py-2 pr-4 text-gray-300 whitespace-nowrap">{formatTime(r.timestamp)}</td>
                    <td className="py-2 pr-4 text-white">
                      <span className="mr-2">{countryToFlag(r.country)}</span>
                      <span className="font-bold">{r.city || '-'}</span>
                      <span className="text-gray-500">
                        {r.region ? `, ${r.region}` : ''}{r.country ? `, ${r.country}` : ''}
                      </span>
                    </td>
                    <td className="py-2 pr-4 text-gray-300 capitalize">{r.device_type || '-'}</td>
                    <td className="py-2 text-gray-400 truncate max-w-[200px]">{r.url || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <button
          onClick={fetchGeo}
          data-testid="geo-refresh-btn"
          className="mt-4 bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded text-sm transition-colors"
        >
          Refresh
        </button>
      </Card>
    </div>
  );
};

export default GeographyTab;
